import { defaultPointKeysBySeriesType, timeSeriesPointKeys } from "../constants";
import type {
  HighchartsWrapperProps,
  OperatorTimeSeriesData,
  OperatorTimeSeriesSeries,
  RecordValue,
} from "../types";
import { isRecord } from "./isRecord";

const serializeTimeSeriesValue = (value: unknown): number | string | null => {
  if (value === null) {
    return null;
  }

  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  return null;
};

const serializeTimeSeriesPoint = (point: unknown): unknown => {
  if (point === null || typeof point === "number" || typeof point === "string") {
    return serializeTimeSeriesValue(point);
  }

  if (!isRecord(point)) {
    return null;
  }

  return timeSeriesPointKeys.reduce<Record<string, unknown>>((serializedPoint, key) => {
    if (point[key] !== undefined) {
      serializedPoint[key] = serializeTimeSeriesValue(point[key]);
    }

    return serializedPoint;
  }, {});
};

const getSeriesXAxis = (seriesOptions: RecordValue, xAxes: RecordValue[]) => {
  const xAxisReference = seriesOptions.xAxis;

  if (typeof xAxisReference === "number") {
    return xAxes[xAxisReference];
  }

  if (typeof xAxisReference === "string") {
    return xAxes.find((xAxis) => xAxis.id === xAxisReference);
  }

  return xAxes[0];
};

const getSeriesPointKeys = (seriesOptions: RecordValue, chartType: unknown): readonly string[] => {
  if (Array.isArray(seriesOptions.keys)) {
    return seriesOptions.keys.filter((key): key is string => typeof key === "string");
  }

  const seriesType = typeof seriesOptions.type === "string" ? seriesOptions.type : chartType;

  if (typeof seriesType === "string" && defaultPointKeysBySeriesType[seriesType]) {
    return defaultPointKeysBySeriesType[seriesType];
  }

  return ["x", "y"];
};

const serializeTimeSeriesData = (data: unknown[], pointKeys: readonly string[]): unknown[] => {
  const allowedPointKeyIndexes = pointKeys.flatMap((key, index) => {
    return timeSeriesPointKeys.includes(key) ? [index] : [];
  });

  return data.map((point) => {
    if (!Array.isArray(point)) {
      return serializeTimeSeriesPoint(point);
    }

    return allowedPointKeyIndexes.map((index) => serializeTimeSeriesValue(point[index]));
  });
};

/**
 * Extracts semantic data from a datetime chart for Operator while omitting
 * presentation options, callbacks, and custom point metadata.
 */
export const getOperatorTimeSeriesData = (
  chartConfig: HighchartsWrapperProps["chartConfig"]
): OperatorTimeSeriesData | undefined => {
  const xAxes = (Array.isArray(chartConfig.xAxis) ? chartConfig.xAxis : [chartConfig.xAxis]).filter(isRecord);
  const hasDatetimeXAxis = xAxes.some((xAxis) => xAxis.type === "datetime");

  if (!hasDatetimeXAxis) {
    return undefined;
  }

  const series = (chartConfig.series || []).flatMap((seriesOptions, index) => {
    if (!isRecord(seriesOptions)) {
      return [];
    }

    const serializableSeriesOptions: Record<string, unknown> = Object.fromEntries(Object.entries(seriesOptions));
    if (!Array.isArray(serializableSeriesOptions.data)) {
      return [];
    }

    const data: unknown[] = serializableSeriesOptions.data;

    if (serializableSeriesOptions.includeInDataExport === false) {
      return [];
    }

    if (getSeriesXAxis(serializableSeriesOptions, xAxes)?.type !== "datetime") {
      return [];
    }

    const pointKeys = getSeriesPointKeys(serializableSeriesOptions, chartConfig.chart?.type);
    const seriesOptionType = serializableSeriesOptions.type;
    const seriesType =
      typeof seriesOptionType === "string"
        ? seriesOptionType
        : typeof chartConfig.chart?.type === "string"
          ? chartConfig.chart.type
          : "line";
    const seriesName =
      typeof serializableSeriesOptions.name === "string" && serializableSeriesOptions.name.length > 0
        ? serializableSeriesOptions.name
        : `Series ${index + 1}`;
    const hasTupleData = data.some(Array.isArray);
    const hasPrimitiveData = data.some(
      (point) => point === null || typeof point === "number" || typeof point === "string"
    );

    const serializedSeries: OperatorTimeSeriesSeries = {
      name: seriesName,
      type: seriesType,
      data: serializeTimeSeriesData(data, pointKeys),
    };

    if (hasTupleData) {
      serializedSeries.keys = pointKeys.filter((key) => timeSeriesPointKeys.includes(key));
    } else if (hasPrimitiveData) {
      serializedSeries.keys = ["y"];
    }

    const pointStart = serializableSeriesOptions.pointStart;
    if (typeof pointStart === "number") {
      serializedSeries.pointStart = pointStart;
    }

    const pointInterval = serializableSeriesOptions.pointInterval;
    if (typeof pointInterval === "number") {
      serializedSeries.pointInterval = pointInterval;
    }

    const pointIntervalUnit = serializableSeriesOptions.pointIntervalUnit;
    if (typeof pointIntervalUnit === "string") {
      serializedSeries.pointIntervalUnit = pointIntervalUnit;
    }

    return [serializedSeries];
  });

  if (series.length === 0) {
    return undefined;
  }

  return {
    type: "time_series",
    xAxis: {
      type: "datetime",
      unit: "milliseconds",
      ...(typeof chartConfig.time?.timezone === "string" && { timezone: chartConfig.time.timezone }),
    },
    series,
  };
};
